import { useTransferOwnershipAccess } from '../../hooks/access/use-transfer-ownership-access';
import OwnerProposalList from '../../components/module/transfer-ownership/common/owner-proposal-list';
import ButtonDropdownOption from '../../components/ui/button-dropdown-option';
import { useDispatch } from 'react-redux';
import { useTransferOwnershipDataState } from '../../hooks/data-state/use-transferOwnership-data-state';
import { setCurrentOwner, setIsTransferFinal, setNewOwner, setSelectedProposals, setShowUserList } from '../../features/transfer-ownership/transfer-ownership-slice';
import { useTransferProposalActions } from '../../hooks/actions/use-transfer-proposal-actions';
import ConfirmDefaultModal from '../../components/global/modal/confirm-default-modal';
import { useDepartmentUserAccess } from '../../hooks/access/use-department-user-access';

const inputStyle = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs text-gray-700 outline-none transition focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-100';

export const statusStyleTransferOwner: Record<string, string> = {
    Draft: 'border-gray-200 bg-gray-50 text-gray-600',
    Checking: 'border-yellow-200 bg-yellow-50 text-yellow-700',
    Pending: 'border-orange-200 bg-orange-50 text-orange-700',
    Approved: 'border-green-200 bg-green-50 text-green-700',
};

export default function TransferOwnershipProposalPage() {

    const dispatch = useDispatch()

    const {
        pagedData,
    } = useTransferOwnershipAccess()
    const {
        formattedUserInActiveOption,
        formattedUserActiveOption,
        isLoadingDepartmentUsers,
        isErrorDepartmentUsers,
    } = useDepartmentUserAccess({departmentType: 'GMS'})
    const {
        isShowCurrentOwner,
        isShowNewOwner,
        formattedCurrentOwner,
        formattedNewOwner,
        selectedProposals,
        ownershipSearchValue,
        isTransferFinal,
    } = useTransferOwnershipDataState()
    const {
        handleTransferProposal,
        isLoadingTransferOwnership,
    } = useTransferProposalActions();

    const canTransfer =
        formattedCurrentOwner.id !== 0 &&
        formattedCurrentOwner.id !== null &&
        formattedNewOwner.id !== 0 &&
        formattedNewOwner.id !== null &&
        formattedCurrentOwner.id !== formattedNewOwner.id &&
        selectedProposals.length > 0;


    const handleTransfer = () => {
        if (!canTransfer || formattedCurrentOwner.id === 0 || formattedNewOwner.id === 0) {
            return;
        }
        dispatch(setIsTransferFinal(true))
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-[#f5f9fd]">
            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}
            <div className="flex flex-shrink-0 flex-col gap-2 border-b border-gray-200 bg-white p-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                            <circle cx="9" cy="7" r="3" />
                            <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
                            <path d="M16 8h5m-2-2 2 2-2 2" />
                            <path d="M21 16h-5m2-2-2 2 2 2" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-sm font-semibold text-gray-800">
                            Transfer Proposal Ownership
                        </h1>
                        <p className="mt-0.5 leading-4 text-gray-500 text-xs">
                            Transfer one or multiple proposals from one user to another.
                        </p>
                    </div>
                </div>
                {selectedProposals.length > 0 && (formattedCurrentOwner.id !== 0 || formattedCurrentOwner.id !== null) && (formattedNewOwner.id !== 0 || formattedNewOwner.id !== null) && (
                    <div className="border-t border-gray-200">
                        <div className="flex pt-2 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-[10px] text-gray-500">
                                    Proposals to be transferred
                                </p>
                                <p className="text-xs font-semibold text-gray-800">
                                    {selectedProposals.length} proposal{selectedProposals.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                disabled={!canTransfer || isLoadingTransferOwnership}
                                onClick={handleTransfer}
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-[#145b96] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isLoadingTransferOwnership ? (
                                    <>
                                        <span className="flex items-center gap-1">
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white [animation-delay:-0.3s]" />
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white [animation-delay:-0.15s]" />
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white" />
                                        </span>
                                        Transferring...
                                    </>
                                ) : (
                                    <>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                                        </svg>
                                        Transfer Ownership
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ================================================= */}
            {/* CONTENT */}
            {/* ================================================= */}
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
                <div className="mx-auto space-y-4">
                    {/* ================================================= */}
                    {/* STEP 1 & 2 */}
                    {/* ================================================= */}
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {/* FROM */}
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 bg-[#eef6fc] px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#186cb2] text-[10px] font-bold text-white">
                                        1
                                    </div>
                                    <div>
                                        <h2 className="text-xs font-semibold text-gray-800">
                                            Current Owner
                                        </h2>
                                        <p className="text-[10px] text-gray-500">
                                            Select the user who currently owns the proposals.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <ButtonDropdownOption
                                    options={formattedUserInActiveOption} 
                                    value={formattedCurrentOwner} 
                                    onChange={(value) => {
                                        dispatch(setCurrentOwner({
                                            id: value.id,
                                            name: value.name,
                                            email: value.subTitle ?? ''
                                        }))
                                        dispatch(setSelectedProposals([]))
                                    }}
                                    open={isShowCurrentOwner}
                                    setOpen={() => dispatch(setShowUserList(isShowCurrentOwner ? null : 'currentOwner'))}
                                    width='w-full'
                                    isSubTitle={true}
                                />
                            </div>
                        </div>

                        {/* TO */}
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 bg-[#eef6fc] px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#186cb2] text-[10px] font-bold text-white">
                                        2
                                    </div>
                                    <div>
                                        <h2 className="text-xs font-semibold text-gray-800">
                                            New Owner
                                        </h2>
                                        <p className="text-[10px] text-gray-500">
                                            Select the user who will receive the proposals.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <ButtonDropdownOption
                                    isDisable={formattedCurrentOwner.id === 0 || formattedCurrentOwner.id === null}
                                    options={formattedUserActiveOption} 
                                    value={formattedNewOwner} 
                                    onChange={(value) => {
                                        dispatch(setNewOwner({
                                            id: value.id,
                                            name: value.name,
                                            email: value.subTitle ?? ''
                                        }))
                                    }}
                                    open={isShowNewOwner}
                                    setOpen={() => dispatch(setShowUserList(isShowNewOwner ? null : 'newOwner'))}
                                    width='w-full'
                                    isSubTitle={true}
                                />
                            </div>
                        </div>
                    </div>
                    {/* ================================================= */}
                    {/* SUCCESS */}
                    {/* ================================================= */}
                    {/* {isSuccess && (
                        <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-white">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-green-800">
                                    Ownership transferred successfully
                                </p>
                                <p className="mt-0.5 text-[10px] leading-4 text-green-700">
                                    {selectedProposals.length} proposal{selectedProposals.length !== 1 ? 's have' : ' has'} been transferred from {formattedCurrentOwner?.name} to {formattedNewOwner?.name}.
                                </p>
                            </div>
                        </div>
                    )} */}
                    {/* ================================================= */}
                    {/* STEP 3 - PROPOSALS */}
                    {/* ================================================= */}
                    <OwnerProposalList
                        selectedProposals={selectedProposals}
                        fromUserId={formattedCurrentOwner.id}
                        ownershipSearchValue={ownershipSearchValue}
                        inputStyle={inputStyle}
                        availableProposals={pagedData}
                        isLoading={isLoadingDepartmentUsers}
                        isError={isErrorDepartmentUsers}
                    />
                </div>
            </div>
            {isTransferFinal && (
                <ConfirmDefaultModal
                    modalTitle={'Confirm Ownership Transfer'}
                    modalSubTitle={`Are you sure you want to transfer ${selectedProposals.length} selected proposal${selectedProposals.length !== 1 ? "s" : ""} from ${formattedCurrentOwner.name} to ${formattedNewOwner.name}? This action will change the proposal owner.`}
                    onCancel={() => dispatch(setIsTransferFinal(false))}
                    onConfirm={handleTransferProposal}
                    isLoading={isLoadingTransferOwnership}
                />
            )}
        </div>
    );
}